import { UserRoleDto } from '@/api/generated';
import Loader from '@/components/Loader.tsx';
import { useUserContext } from '@/utils/providers/userContext/useUserContext.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { CandidatesTab } from '@/views/admin/candidates/CandidatesTab.comp.tsx';
import { FairsTab } from '@/views/admin/fairs/FairsTab.comp.tsx';
import { type ReportPeriod } from '@/views/admin/reports/reportPeriod.ts';
import { ReportsTab } from '@/views/admin/reports/ReportsTab.comp.tsx';
import { useLogout } from '@/views/admin/util/useLogout.util.ts';
import React, { lazy, Suspense, useState } from 'react';
import { Button, Nav, Tab } from 'react-bootstrap';
import { Navigate } from 'react-router-dom';

// ECharts and the map are loaded only when an admin opens the panel, not on the candidate path
const DashboardTab = lazy(() => import('@/views/admin/dashboard/DashboardTab.comp.tsx'));

const AdminView: React.FC = () => {
  const { isInitialized, user } = useUserContext();

  if (!isInitialized) {
    return <Loader className="min-h-dvh" />;
  }

  if (user?.role !== UserRoleDto.Admin) {
    return <Navigate replace to="/409?errorCode=409_closed" />;
  }

  return <AdminInnerView />;
};

interface PrintRequest {
  id: number;
  period: ReportPeriod;
}

const AdminInnerView: React.FC = () => {
  const { t } = useTranslationWithPrefix('views.admin');
  const { logout } = useLogout();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [printRequest, setPrintRequest] = useState<PrintRequest>();

  // the reports print the dashboard: it opens again, with the period of the reports, and prints itself
  const printDashboard = (period: ReportPeriod) => {
    setPrintRequest({ id: Date.now(), period });
    setActiveTab('dashboard');
  };

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderBottom: '1px solid #dee2e6',
        maxWidth: 'none',
        paddingLeft: '15px',
        paddingTop: '10px',
        width: '100%',
      }}
    >
      {/* the tabs are mounted when first opened, so a tab's data is fetched only when it is used */}
      <Tab.Container activeKey={activeTab} mountOnEnter onSelect={key => key && setActiveTab(key)}>
        <Nav className="print:hidden" variant="tabs">
          <Nav.Item>
            <Nav.Link eventKey="dashboard">{t('tabs.dashboard')}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="candidates">{t('tabs.candidates')}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="fairCandidates">{t('tabs.fairCandidates')}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="reports">{t('tabs.reports')}</Nav.Link>
          </Nav.Item>
          {/* at the end of the tabs, not fixed over the header, where it used to cover the language switcher */}
          <Nav.Item className="ms-auto d-flex align-items-center pe-3">
            <Button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => void logout()}
              type="button"
            >
              {t('buttons.logout')}
            </Button>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="dashboard">
            <Suspense fallback={<Loader className="min-h-[60vh]" />}>
              <DashboardTab
                initialPeriod={printRequest?.period}
                key={printRequest?.id}
                printOnLoad={!!printRequest}
              />
            </Suspense>
          </Tab.Pane>

          <Tab.Pane eventKey="candidates">
            <CandidatesTab />
          </Tab.Pane>

          <Tab.Pane eventKey="fairCandidates">
            <FairsTab />
          </Tab.Pane>

          <Tab.Pane eventKey="reports">
            <ReportsTab onPrintDashboard={printDashboard} />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default AdminView;
